/**
 * ClubMembersPage - Club Member Grid
 *
 * Displays club members in a grid layout with avatars, names, and role badges.
 * Rebuilt to match Figma Nightfall Kit design with clean grid layout.
 *
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7C
 * Issue #1227: Unified with settings/members - leaders see management actions
 * Issue #1228: Clickable cards link to user profile
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClub } from '../hooks/useClubs';
import { useClubMembers, useRemoveMember, useUpdateMemberRole } from '../hooks/useMembers';
import { TopBar, Avatar, RoleBadge, GridSkeleton, NoMembersEmptyState, EmptyState, Button, Card } from '../components';
import { MoreVertical, UserCog, UserX, AlertTriangle } from 'lucide-react';

export default function ClubMembersPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Use React Query hooks
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const { data: membersData, isLoading: membersLoading, error: membersError } = useClubMembers(slug || '');

  // Member management mutations (#1227)
  const removeMemberMutation = useRemoveMember(slug || '');
  const updateMemberRoleMutation = useUpdateMemberRole(slug || '');

  // Dialog state for member management (#1227)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [showActions, setShowActions] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState<'leader' | 'member'>('member');

  const isLoading = clubLoading || membersLoading;
  const error = clubError || membersError;

  // Check permissions
  const isLeader = clubData?.membership?.role === 'leader' || clubData?.membership?.is_owner === true;
  const isMember = !!clubData?.membership; // Any club member can view member list (#1028)
  const club = clubData?.club;
  const members = membersData?.members || [];
  const ownerId = club?.owner_id;

  // Get selected member for dialogs
  const selectedMember = members.find(m => m.user_id === selectedMemberId);

  // Handlers for member management
  const handleOpenActions = (memberId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't navigate to profile
    setSelectedMemberId(memberId);
    setShowActions(true);
  };

  const handleRemoveMember = async () => {
    if (!selectedMemberId) return;
    try {
      await removeMemberMutation.mutateAsync(selectedMemberId);
      setShowRemoveConfirm(false);
      setShowActions(false);
      setSelectedMemberId(null);
    } catch {
      // Error toast handled by useOroMutation
    }
  };

  const handleChangeRole = async () => {
    if (!selectedMemberId) return;
    try {
      await updateMemberRoleMutation.mutateAsync({ userId: selectedMemberId, role: newRole });
      setShowRoleDialog(false);
      setShowActions(false);
      setSelectedMemberId(null);
    } catch {
      // Error toast handled by useOroMutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar
          title="Members"
          onBack={() => navigate(-1)}
        />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6">
            {/* Professional grid skeleton */}
            <GridSkeleton items={6} columns={2} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !isMember) {
    const errorMessage = error
      ? (error instanceof Error ? error.message : 'Unable to load members')
      : 'You must be a club member to view the member list';
      
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar
          title="Members"
          onBack={() => navigate(-1)}
        />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<AlertTriangle className="w-full h-full" />}
              title="Cannot load members"
              message={errorMessage}
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar
        title="Members"
        onBack={() => navigate(-1)}
      />
      
      <div className="flex-1 overflow-y-auto pt-14">
        <div className="px-4 py-6 space-y-6">
          {/* Header */}
          <div className="text-center space-y-1">
            <p className="text-xs uppercase tracking-[0.35em] text-text-muted">
              {club?.name}
            </p>
            <p className="text-text-secondary">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>
          </div>

          {/* Empty State */}
          {members.length === 0 && (
            <div className="py-8">
              <NoMembersEmptyState 
                variant="default"
                onAction={() => navigate(`/clubs/${slug}/invite`)}
              />
            </div>
          )}

          {/* Member Grid (#1228: Clickable cards, #1227: Leader actions) */}
          {members.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {members.map((member) => {
                const isOwner = member.user_id === ownerId;
                const canManage = isLeader && !isOwner;

                return (
                  <div key={member.user_id} className="relative group">
                    <button
                      onClick={() => navigate(`/u/${member.user_id}`, { state: { club } })}
                      className="w-full bg-oro-surface border border-oro-border rounded-2xl p-4 flex flex-col items-center text-center hover:border-accent/30 active:bg-oro-surface-alt transition-colors shadow-card cursor-pointer"
                    >
                      {/* Avatar */}
                      <Avatar
                        name={member.public_display_name}
                        profile_image_url={member.profile_image_url}
                        size="lg"
                        className="mb-3"
                      />

                      {/* Name with Owner badge */}
                      <div className="flex items-center gap-1 mb-1">
                        <h3 className="text-sm font-semibold text-text-primary line-clamp-1">
                          {member.public_display_name}
                        </h3>
                        {isOwner && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-accent/20 text-accent rounded-full font-medium shrink-0">
                            Owner
                          </span>
                        )}
                      </div>

                      {/* Role Badge */}
                      <RoleBadge role={member.role} size="xs" />
                    </button>

                    {/* 3-dot menu for leaders (#1227) - always visible on mobile, hover on desktop */}
                    {canManage && (
                      <button
                        onClick={(e) => handleOpenActions(member.user_id, e)}
                        className="absolute top-2 right-2 p-2 rounded-lg md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 hover:bg-oro-surface-alt text-text-muted hover:text-text-secondary transition-all"
                        aria-label={`Manage ${member.public_display_name}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Action Sheet (#1227) */}
      {showActions && selectedMember && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 50 }}
            onClick={() => {
              setShowActions(false);
              setSelectedMemberId(null);
            }}
            aria-hidden="true"
          />

          {/* Bottom Sheet */}
          <div
            className="fixed inset-x-0 bottom-0 bg-oro-surface border-t border-oro-border rounded-t-2xl shadow-floating animate-in slide-in-from-bottom duration-200"
            style={{ zIndex: 60 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="member-actions-title"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-oro-border rounded-full" />
            </div>

            <div className="px-4 pb-8 pt-2">
              <h3 id="member-actions-title" className="font-semibold text-text-primary mb-1">
                {selectedMember.public_display_name}
              </h3>
              <p className="text-sm text-text-muted mb-4">
                <RoleBadge role={selectedMember.role} size="xs" />
              </p>

              <div className="space-y-1">
                <button
                  onClick={() => {
                    setNewRole(selectedMember.role === 'leader' ? 'member' : 'leader');
                    setShowActions(false);
                    setShowRoleDialog(true);
                  }}
                  className="flex items-center gap-3 w-full p-4 rounded-lg text-text-primary hover:bg-oro-surface-alt transition-colors"
                >
                  <UserCog className="w-5 h-5 text-accent" />
                  <span className="font-medium">Change Role</span>
                </button>

                <button
                  onClick={() => {
                    setShowActions(false);
                    setShowRemoveConfirm(true);
                  }}
                  className="flex items-center gap-3 w-full p-4 rounded-lg text-oro-error hover:bg-oro-error/10 transition-colors"
                >
                  <UserX className="w-5 h-5" />
                  <span className="font-medium">Remove from Club</span>
                </button>

                <button
                  onClick={() => {
                    setShowActions(false);
                    setSelectedMemberId(null);
                  }}
                  className="flex items-center justify-center w-full p-4 rounded-lg text-text-secondary hover:bg-oro-surface-alt transition-colors"
                >
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Remove Member Confirmation Dialog */}
      {showRemoveConfirm && selectedMember && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Remove Member</h3>
            <p className="text-text-secondary mb-6">
              Are you sure you want to remove <span className="font-medium text-text-primary">{selectedMember.public_display_name}</span>?
              They will lose access to the club immediately.
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setSelectedMemberId(null);
                }}
                disabled={removeMemberMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRemoveMember}
                loading={removeMemberMutation.isPending}
                className="bg-oro-error hover:bg-oro-error/90"
              >
                Remove Member
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Change Role Dialog */}
      {showRoleDialog && selectedMember && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Change Member Role</h3>
            <p className="text-text-secondary mb-4">
              Update role for <span className="font-medium text-text-primary">{selectedMember.public_display_name}</span>:
            </p>

            <div className="space-y-2 mb-6">
              {(['member', 'leader'] as const).map((role) => (
                <label
                  key={role}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    newRole === role
                      ? 'border-accent bg-accent/5'
                      : 'border-oro-border hover:border-oro-border-hover'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={newRole === role}
                    onChange={(e) => setNewRole(e.target.value as 'leader' | 'member')}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      newRole === role ? 'border-accent' : 'border-oro-border'
                    }`}
                  >
                    {newRole === role && (
                      <div className="w-2 h-2 rounded-full bg-accent" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <RoleBadge role={role} size="xs" />
                    <div>
                      <p className="font-medium capitalize">{role}</p>
                      <p className="text-xs text-text-muted">
                        {role === 'leader' && 'Full club management access'}
                        {role === 'member' && 'Basic club access'}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRoleDialog(false);
                  setSelectedMemberId(null);
                }}
                disabled={updateMemberRoleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleChangeRole}
                loading={updateMemberRoleMutation.isPending}
              >
                Update Role
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
